import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import DialogShell from '../../tasks/components/DialogShell';
import { errorText, Group, groupPath, groupTree, isRevenue, Ledger, LedgerSave, ledgersApi, natureLabel } from '../accounting.api';

interface Form {
  groupId: number | null;
  name: string;
  code: string;
  openingBalance: string;
  openingSide: 'DR' | 'CR';
  bankAccountNo: string;
  bankIfsc: string;
  bankBranch: string;
  gstin: string;
  pan: string;
  address: string;
  active: boolean;
}

const toForm = (l: Ledger | null): Form => ({
  groupId: l?.groupId ?? null,
  name: l?.name ?? '',
  code: l?.code ?? '',
  openingBalance: l && Number(l.openingBalance) ? Number(l.openingBalance).toFixed(2) : '',
  openingSide: l?.openingSide ?? 'DR',
  bankAccountNo: l?.bankAccountNo ?? '',
  bankIfsc: l?.bankIfsc ?? '',
  bankBranch: l?.bankBranch ?? '',
  gstin: l?.gstin ?? '',
  pan: l?.pan ?? '',
  address: l?.address ?? '',
  active: l?.active ?? true,
});

const orNull = (s: string) => s.trim() || null;

/**
 * Add or edit a ledger. Income and expense ledgers start every year at nil, so they take no
 * opening balance; bank details appear for ledgers under a bank group.
 */
export default function LedgerDialog({
  open,
  companyId,
  ledger,
  groups,
  onClose,
  onSaved,
}: {
  open: boolean;
  companyId: number | null;
  /** null adds a new one. */
  ledger: Ledger | null;
  groups: Group[];
  onClose: () => void;
  onSaved: (l: Ledger) => void;
}) {
  const [form, setForm] = useState<Form>(toForm(ledger));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(toForm(ledger));
  }, [open, ledger]);

  const tree = useMemo(() => groupTree(groups), [groups]);
  const group = groups.find((g) => g.id === form.groupId) ?? null;
  const revenue = isRevenue(group?.nature);
  const bank = group?.cashBank === 'BANK';
  const set = (p: Partial<Form>) => setForm((f) => ({ ...f, ...p }));

  // Assets and expenses normally sit on the debit side, the rest on the credit side.
  const pickGroup = (g: Group | null) =>
    set({ groupId: g?.id ?? null, ...(g && !ledger ? { openingSide: g.nature === 'ASSET' || g.nature === 'EXPENSE' ? 'DR' : 'CR' } : {}) });

  const save = async () => {
    if (!companyId || !form.groupId || !form.name.trim()) return;
    const body: LedgerSave = {
      groupId: form.groupId,
      name: form.name.trim(),
      code: orNull(form.code),
      openingBalance: revenue ? 0 : Number(form.openingBalance || 0),
      openingSide: form.openingSide,
      bankAccountNo: bank ? orNull(form.bankAccountNo) : null,
      bankIfsc: bank ? orNull(form.bankIfsc) : null,
      bankBranch: bank ? orNull(form.bankBranch) : null,
      gstin: orNull(form.gstin),
      pan: orNull(form.pan),
      address: orNull(form.address),
      active: form.active,
    };
    setBusy(true);
    try {
      const saved = ledger ? await ledgersApi.update(companyId, ledger.id, body) : await ledgersApi.create(companyId, body);
      toast.success(ledger ? 'Ledger saved.' : 'Ledger created.');
      onSaved(saved);
      onClose();
    } catch (e) {
      toast.error(errorText(e, 'Could not save the ledger', 'ACCOUNTING_LEDGER_SAVE_FAILED'));
    } finally {
      setBusy(false);
    }
  };

  const validAmount = /^\d{0,13}(\.\d{0,2})?$/;

  return (
    <DialogShell
      open={open}
      title={ledger ? 'EDIT LEDGER' : 'NEW LEDGER'}
      onClose={onClose}
      onSubmit={save}
      busy={busy}
      disabled={!form.groupId || !form.name.trim()}
      maxWidth='md'
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 2 }}>
        <TextField label='Name' size='small' required autoFocus value={form.name} inputProps={{ maxLength: 150 }} onChange={(e) => set({ name: e.target.value })} />
        <TextField label='Code' size='small' value={form.code} inputProps={{ maxLength: 30 }} onChange={(e) => set({ code: e.target.value })} />
      </Box>
      <Autocomplete
        size='small'
        options={tree.map((t) => t.group)}
        value={group}
        onChange={(_, g) => pickGroup(g)}
        getOptionLabel={(g) => groupPath(groups, g.id)}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        disabled={ledger?.system}
        renderOption={(props, g) => {
          const { key, ...rest } = props as typeof props & { key: string };
          const depth = tree.find((t) => t.group.id === g.id)?.depth ?? 0;
          return (
            <li key={key} {...rest}>
              <Box sx={{ pl: depth * 2 }}>
                <Typography variant='body2' fontWeight={depth === 0 ? 700 : 400}>
                  {g.name}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {natureLabel(g.nature)}
                  {g.cashBank ? ` · ${g.cashBank === 'CASH' ? 'Cash' : 'Bank'}` : ''}
                </Typography>
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label='Under group'
            required
            helperText={group ? `${natureLabel(group.nature)}${group.cashBank ? ` · ${group.cashBank === 'CASH' ? 'cash' : 'bank'} ledger` : ''}` : ' '}
          />
        )}
      />

      {revenue ? (
        <Typography variant='body2' color='text.secondary'>
          Income and expense ledgers start every financial year at nil, so they have no opening balance.
        </Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '2fr 1fr', sm: '220px 110px' }, gap: 2 }}>
          <TextField
            label='Opening balance'
            size='small'
            value={form.openingBalance}
            placeholder='0.00'
            disabled={!group}
            inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
            onChange={(e) => validAmount.test(e.target.value) && set({ openingBalance: e.target.value })}
            helperText='As on the books’ beginning date'
          />
          <TextField
            select
            label='Side'
            size='small'
            value={form.openingSide}
            disabled={!group}
            onChange={(e) => set({ openingSide: e.target.value as 'DR' | 'CR' })}
          >
            <MenuItem value='DR'>Dr</MenuItem>
            <MenuItem value='CR'>Cr</MenuItem>
          </TextField>
        </Box>
      )}

      {bank && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
          <TextField label='Account no.' size='small' value={form.bankAccountNo} inputProps={{ maxLength: 40 }} onChange={(e) => set({ bankAccountNo: e.target.value })} />
          <TextField
            label='IFSC'
            size='small'
            value={form.bankIfsc}
            inputProps={{ maxLength: 20 }}
            onChange={(e) => set({ bankIfsc: e.target.value.toUpperCase() })}
            placeholder='HDFC0001234'
          />
          <TextField label='Branch' size='small' value={form.bankBranch} inputProps={{ maxLength: 100 }} onChange={(e) => set({ bankBranch: e.target.value })} />
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <TextField label='GSTIN' size='small' value={form.gstin} inputProps={{ maxLength: 20 }} onChange={(e) => set({ gstin: e.target.value.toUpperCase() })} />
        <TextField label='PAN' size='small' value={form.pan} inputProps={{ maxLength: 20 }} onChange={(e) => set({ pan: e.target.value.toUpperCase() })} />
      </Box>
      <TextField label='Address' size='small' multiline minRows={2} value={form.address} inputProps={{ maxLength: 500 }} onChange={(e) => set({ address: e.target.value })} />
      <FormControlLabel
        control={<Switch checked={form.active} onChange={(e) => set({ active: e.target.checked })} />}
        label={form.active ? 'Active' : 'Inactive — cannot be used in new vouchers'}
      />
    </DialogShell>
  );
}
